import { formatAppDate } from "./appTimeFormat";

export const norm = (s) => String(s || "").toLowerCase().trim();

export function leagueSearchBlob(league) {
  const parts = [
    league?.leagueName,
    league?.countryName,
    league?.name,
    league?.country?.name,
  ];
  const fixtures = Array.isArray(league?.fixtures) ? league.fixtures : [];
  for (let i = 0; i < fixtures.length; i += 1) {
    const f = fixtures[i];
    parts.push(f?.homeTeam?.name, f?.awayTeam?.name);
  }
  return parts.map(norm).filter(Boolean).join("\u0000");
}

export function leagueMatchesQuery(league, queryLowerTokens) {
  if (!queryLowerTokens.length) return true;
  const blob = leagueSearchBlob(league);
  return queryLowerTokens.every((t) => blob.includes(t));
}

export function searchTokensFromQuery(searchQuery) {
  return norm(searchQuery).split(/\s+/).filter(Boolean);
}

export function fixtureMatchesSearch(game, tokens) {
  if (!tokens.length) return true;
  const home = norm(game?.homeTeam?.name);
  const away = norm(game?.awayTeam?.name);
  const blob = `${home}\u0000${away}`;
  return tokens.every((t) => blob.includes(t));
}

const UPCOMING_STATUS_SHORT = new Set(["NS", "TBD"]);

const LIVE_STATUS_SHORT = new Set(["1H", "2H", "HT", "ET", "BT", "LIVE", "P", "INT"]);
const FINISHED_STATUS_SHORT = new Set(["FT", "AET", "PEN", "FT_PEN"]);

/** P2P creation/edit closes this long before kickoff. Counters close 5 minutes before kickoff. */
export const P2P_CREATE_BUFFER_MS = 10 * 60 * 1000;

export function fixtureTimestampMs(fixture) {
  const raw = fixture?.timestamp;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    return n > 1e12 ? n : n * 1000;
  }
  if (fixture?.date) {
    const ms = new Date(fixture.date).getTime();
    if (Number.isFinite(ms) && !Number.isNaN(ms)) {
      return ms;
    }
  }
  return null;
}

export function isUpcomingOrLiveFixture(fixture, nowMs = Date.now()) {
  if (!fixture) return false;
  const statusShort = fixture?.status?.short;
  if (statusShort && !UPCOMING_STATUS_SHORT.has(statusShort)) return false;
  const tsMs = fixtureTimestampMs(fixture);
  if (!tsMs) return false;
  return tsMs >= nowMs;
}

/** Fixture has not kicked off yet — NS/TBD only, kickoff strictly in the future. */
export function isUpcomingFixture(fixture, nowMs = Date.now()) {
  if (!fixture) return false;
  if (isFixtureLive(fixture)) return false;
  const statusShort = fixture?.status?.short;
  if (statusShort && !UPCOMING_STATUS_SHORT.has(statusShort)) return false;
  const tsMs = fixtureTimestampMs(fixture);
  if (!tsMs) return false;
  return tsMs > nowMs;
}

export function filterUpcomingOrLiveFixtures(fixtures, nowMs = Date.now()) {
  if (!Array.isArray(fixtures)) return [];
  return fixtures.filter((f) => isUpcomingOrLiveFixture(f, nowMs));
}

export function filterUpcomingFixtures(fixtures, nowMs = Date.now()) {
  if (!Array.isArray(fixtures)) return [];
  return fixtures.filter((f) => isUpcomingFixture(f, nowMs));
}

export function isFixtureLive(game) {
  const s = (game?.status?.short || "").toUpperCase();
  return LIVE_STATUS_SHORT.has(s);
}

export function isFixtureFinished(game) {
  const s = (game?.status?.short || "").toUpperCase();
  return FINISHED_STATUS_SHORT.has(s);
}

export function isFixtureLiveOrFinished(game) {
  return isFixtureLive(game) || isFixtureFinished(game);
}

/**
 * Fixture eligible for new P2P offers — not started, not live, not finished,
 * and at least 10 minutes before kickoff (create window; counters close at 5 minutes).
 */
export function isP2PCreateEligibleFixture(fixture, nowMs = Date.now()) {
  if (!fixture || isFixtureLiveOrFinished(fixture)) return false;
  if (!isUpcomingFixture(fixture, nowMs)) return false;
  const tsMs = fixtureTimestampMs(fixture);
  if (!tsMs) return false;
  return tsMs - nowMs >= P2P_CREATE_BUFFER_MS;
}

export function filterP2PCreateEligibleFixtures(fixtures, nowMs = Date.now()) {
  if (!Array.isArray(fixtures)) return [];
  return fixtures.filter((f) => isP2PCreateEligibleFixture(f, nowMs));
}

/**
 * Whether post-offer (+/Challenge) actions may be shown for a fixture.
 * Blocks live, finished, past kickoff, and the 1-hour pre-kickoff window.
 */
export function canShowP2PPostOfferOnFixture(fixture, nowMs = Date.now()) {
  if (!fixture || isFixtureLiveOrFinished(fixture)) return false;
  return isP2PCreateEligibleFixture(fixture, nowMs);
}

export function getLeagueId(league) {
  return league?._id || league?.id;
}

export function getFixtureId(fixture) {
  return fixture?._id || fixture?.id;
}

const ONE_HOUR = 60 * 60 * 1000;
const ONE_DAY = 24 * ONE_HOUR;

function startOfDay(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Flatten a leagues payload into a sorted-by-kickoff list of fixtures with
 * a small `league` summary attached for badges.
 */
export function flattenLeaguesToFixtures(leagues, nowMs = Date.now(), options = {}) {
  const { upcomingOnly = false, p2pCreateEligibleOnly = false } = options;
  let filterFn = filterUpcomingOrLiveFixtures;
  if (p2pCreateEligibleOnly) filterFn = filterP2PCreateEligibleFixtures;
  else if (upcomingOnly) filterFn = filterUpcomingFixtures;
  if (!Array.isArray(leagues)) return [];
  const rows = [];
  for (const league of leagues) {
    const leagueSummary = {
      id: getLeagueId(league),
      name: league?.leagueName || league?.name || "League",
      logo: league?.leagueLogo || null,
      country: league?.countryName || league?.country?.name || "",
    };
    const fixtures = filterFn(league?.fixtures, nowMs);
    for (const fixture of fixtures) {
      rows.push({ fixture, league: leagueSummary });
    }
  }
  rows.sort((a, b) => {
    const ta = fixtureTimestampMs(a.fixture) || 0;
    const tb = fixtureTimestampMs(b.fixture) || 0;
    return ta - tb;
  });
  return rows;
}

/** Time-bucket key for a fixture, used by the kickoff feed chips. */
export function bucketForFixture(fixture, nowMs = Date.now()) {
  if (isFixtureLive(fixture)) return "live";
  const ts = fixtureTimestampMs(fixture);
  if (!ts) return "later";
  const today = startOfDay(nowMs);
  const tomorrow = today + ONE_DAY;
  const weekEnd = today + 7 * ONE_DAY;
  const fixDay = startOfDay(ts);
  if (fixDay <= today) return "today";
  if (fixDay < tomorrow + ONE_DAY) return "tomorrow";
  if (fixDay < weekEnd) return "week";
  return "later";
}

/** Friendly relative-day label for grouping headers. */
export function dayHeadingForFixture(fixture, nowMs = Date.now()) {
  const ts = fixtureTimestampMs(fixture);
  if (!ts) return "Later";
  const today = startOfDay(nowMs);
  const fixDay = startOfDay(ts);
  if (fixDay <= today) return "Today";
  if (fixDay === today + ONE_DAY) return "Tomorrow";
  return formatAppDate(ts);
}

/** Tokens to match against a flattened fixture row (team names + league + country). */
export function rowMatchesQuery(row, tokens) {
  if (!tokens.length) return true;
  const parts = [
    row?.fixture?.homeTeam?.name,
    row?.fixture?.awayTeam?.name,
    row?.league?.name,
    row?.league?.country,
  ];
  const blob = parts.map(norm).filter(Boolean).join("\u0000");
  return tokens.every((t) => blob.includes(t));
}
